import { crudApi, type CrudRecord } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { hoApi } from '../api';

type Schemas = HoComponents['schemas'];
export type DeliveryProvider = Schemas['ProviderDto'];
export type ProviderSetting = Schemas['SettingDto'];
export type ProviderAccount = Schemas['ProviderAccountDto'];
export type ProviderAccountInput = Schemas['ProviderAccountInput'];
export type AccountHealth = Schemas['ProviderAccountHealthDto'];
export type DeliveryServiceRow = Schemas['DeliveryServiceDto'];
export type DeliveryServiceInput = Schemas['DeliveryServiceInput'];
export type Assignment = Schemas['DeliveryServiceAssignmentDto'];
export type AssignmentInput = Schemas['DeliveryServiceAssignmentInput'];

/**
 * What a provider does for us: the hub keeps each capability's providers and accounts apart, with the same shapes (ADR 0025,
 * ADR 0048, ADR 0072, ADR 0074).
 */
export type ProviderCapability = 'shipping' | 'einvoice' | 'messaging' | 'payment';

// Every capability has the same routes; the generated types name them under shipping.
const route = <P extends string>(capability: ProviderCapability, rest: P) =>
  `/api/v1/integration/${capability}${rest}` as `/api/v1/integration/shipping${P}`;

/** The provider catalogue and the accounts of one capability. */
export function providerApi(capability: ProviderCapability) {
  return {
    providersKey: ['ho', capability, 'providers'] as const,
    accountsKey: ['ho', capability, 'accounts'] as const,

    async listProviders(): Promise<DeliveryProvider[]> {
      const { data, error } = await hoApi.GET(route(capability, '/providers'));
      if (!data) throw error;
      return data;
    },

    async listAccounts(): Promise<ProviderAccount[]> {
      const { data, error } = await hoApi.GET(route(capability, '/accounts'));
      if (!data) throw error;
      return data.items;
    },

    async saveAccount(id: string | undefined, input: ProviderAccountInput): Promise<ProviderAccount> {
      const result = id
        ? await hoApi.PUT(route(capability, '/accounts/{id}'), { params: { path: { id } }, body: input })
        : await hoApi.POST(route(capability, '/accounts'), { body: input });
      if (!result.data) throw result.error;
      return result.data;
    },

    async deleteAccount(id: string): Promise<void> {
      const { error, response } = await hoApi.DELETE(route(capability, '/accounts/{id}'), { params: { path: { id } } });
      if (!response.ok) throw error;
    },

    /** Sets credentials; a null value removes one. Returns the names that are set, never the values. */
    async setCredentials(id: string, values: Record<string, string | null>): Promise<string[]> {
      const { data, error } = await hoApi.PUT(route(capability, '/accounts/{id}/credentials'), {
        params: { path: { id } },
        // A null value removes a credential; the generated type only knows strings.
        body: { values: values as Record<string, string> },
      });
      if (!data) throw error;
      return data;
    },

    healthKey: ['ho', capability, 'accounts', 'health'] as const,

    /** Each account's last hour from the provider call log (ADR 0113). */
    async listHealth(): Promise<AccountHealth[]> {
      const { data, error } = await hoApi.GET(route(capability, '/accounts/health'));
      if (!data) throw error;
      return Array.isArray(data) ? data : [];
    },

    async testAccount(id: string) {
      const { data, error } = await hoApi.POST(route(capability, '/accounts/{id}/test'), { params: { path: { id } } });
      if (!data) throw error;
      return data;
    },
  };
}

/** Delivery's providers and accounts (ADR 0025). */
export const { providersKey, accountsKey, listProviders, listAccounts } = providerApi('shipping');
export const servicesKey = ['ho', 'delivery', 'services'] as const;

type ServiceRecord = DeliveryServiceRow & CrudRecord;
type AssignmentRecord = Assignment & CrudRecord;

export const servicesApi = crudApi<ServiceRecord, DeliveryServiceInput>(hoApi, '/api/v1/menu/config/delivery-services');

export const assignmentsApi = (serviceId: string) =>
  crudApi<AssignmentRecord, AssignmentInput>(hoApi, `/api/v1/menu/config/delivery-services/${serviceId}/assignments`);

export type DeliveryProfile = Schemas['DeliveryProfileDto'];
export type DeliveryProfileInput = Schemas['DeliveryProfileInput'];

/** A delivery service's integration profiles (ADR 0112): which account books it for a brand, a store or everyone. */
export const profilesKey = (serviceId: string) => ['ho', 'delivery', 'profiles', serviceId] as const;

export async function listProfiles(serviceId: string): Promise<DeliveryProfile[]> {
  const { data, error } = await hoApi.GET('/api/v1/integration/shipping/services/{serviceId}/profiles', {
    params: { path: { serviceId } },
  });
  if (!data) throw error;
  return data;
}

export async function saveProfile(serviceId: string, id: string | undefined, input: DeliveryProfileInput): Promise<DeliveryProfile> {
  const result = id
    ? await hoApi.PUT('/api/v1/integration/shipping/services/{serviceId}/profiles/{id}', { params: { path: { serviceId, id } }, body: input })
    : await hoApi.POST('/api/v1/integration/shipping/services/{serviceId}/profiles', { params: { path: { serviceId } }, body: input });
  if (!result.data) throw result.error;
  return result.data;
}

export async function deleteProfile(serviceId: string, id: string): Promise<void> {
  const { error, response } = await hoApi.DELETE('/api/v1/integration/shipping/services/{serviceId}/profiles/{id}', {
    params: { path: { serviceId, id } },
  });
  if (!response.ok) throw error;
}

/** The organisation's legal entities: an e-invoice account issues for one (ADR 0048). */
export type LegalEntity = CrudRecord & { code: string; legalName: string; taxCode: string; isActive: boolean };
export const legalEntitiesKey = ['ho', 'org', 'legal-entities', 'all'] as const;
export const legalEntitiesApi = crudApi<LegalEntity, never>(hoApi, '/api/v1/identity/legal-entities');

/** Every brand of the tenant, for pickers (a messaging account or template belongs to one). */
export const brandsKey = ['ho', 'org', 'brands', 'all'] as const;

/** Names for the scope pickers: brands, stores, POS profiles and sale types. */
export const scopeApis = {
  brands: crudApi<CrudRecord & { name: string }, never>(hoApi, '/api/v1/identity/brands'),
  stores: crudApi<CrudRecord & { name: string }, never>(hoApi, '/api/v1/identity/stores'),
  profiles: crudApi<CrudRecord & { name: string }, never>(hoApi, '/api/v1/menu/config/pos-profiles'),
  saleTypes: crudApi<CrudRecord & { name: string }, never>(hoApi, '/api/v1/menu/config/sale-types'),
};

/** Every record of a list: pages through it, so no list is silently cut at one page. */
export async function listAllPages<T extends CrudRecord>(
  api: { list(params: { page: number; pageSize: number }): Promise<{ items: T[]; total: number }> },
  pageSize = 100,
): Promise<T[]> {
  const items: T[] = [];
  for (let page = 1; ; page++) {
    const result = await api.list({ page, pageSize });
    items.push(...result.items);
    if (items.length >= result.total || result.items.length === 0) return items;
  }
}
